import PubSub from 'pubsub-js';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { NotificationType } from '../api';
import { FlutterMeta, OneMonitoringData, PushContext, DownloadFileContext } from '../flutterInterface';
import { UploadFileContext } from '../flutterInterface/actions';
import { useRuntimeConfig } from '../runtimeConfig';
import { LocationHistory } from './LocationHistory';

export type FlutterContext = FlutterMeta & {
    requestOneMonitoring: () => Promise<OneMonitoringData>;
    initializePush: () => Promise<PushContext>;
    checkBiometric: () => Promise<boolean>;
    sendDownloadFile: ({ filename, signedUrl }: DownloadFileContext) => Promise<boolean>;
    uploadFile: ({ allowedExtensions, allowedMultiple }: UploadFileContext) => Promise<File[]>;
    uploadImage: ({ allowedExtensions, allowedMultiple }: UploadFileContext) => Promise<File[]>;
};

const Context = createContext<FlutterContext | null>(null);

export type FlutterProviderProps = {
    children: JSX.Element | ReactNode;
};

export const useFlutter = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('Flutter context not provided');
    }

    return context;
};

const FlutterProvider = ({ children }: FlutterProviderProps) => {
    const { flutterInterface } = useRuntimeConfig();
    const history = useHistory();
    const location = useLocation();

    const timestamp = new Date().valueOf();
    const locationState = useMemo(
        () =>
            ({
                ...(location.state as LocationHistory),
                refreshPurchaseTransactionDetail: timestamp,
                refreshSalesTransactionDetail: timestamp,
                refreshBiddingList: timestamp,
            } as LocationHistory),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.state]
    );

    const [meta, setMeta] = useState<FlutterMeta>({
        deviceFingerPrint: null,
        capabilities: {
            hasFingerPrint: false,
            hasOneMonitoring: false,
            hasFaceID: false,
        },
    });

    useEffect(() => {
        let mounted = true;

        flutterInterface.initialize().then(response => {
            if (mounted) {
                setMeta(response);
            }
        });

        return () => {
            mounted = false;
        };
    }, [setMeta, flutterInterface]);

    const checkIfDuplicatePathName = useCallback(
        (input: string) => history.location.pathname.includes(input),
        [history.location.pathname]
    );

    useEffect(() => {
        // todo later do actions on push received
        const token = flutterInterface.listenPushes(push => {
            const notificationType = push.type;
            if (notificationType) {
                switch (notificationType.toLowerCase()) {
                    case NotificationType.PendingValuationNotice.toLowerCase():
                    case NotificationType.PendingRequoteNotice.toLowerCase():
                    case NotificationType.HandoverDateChangeNotice.toLowerCase():
                    case NotificationType.PurchaseTransactionAmendedNotice.toLowerCase():
                    case NotificationType.PendingAcceptanceNotice.toLowerCase():
                    case NotificationType.PurchaseTransactionApprovalNotice.toLowerCase():
                    case NotificationType.PurchaseTransactionApprovedNotice.toLowerCase():
                    case NotificationType.PurchaseTransactionValuationAcceptedNotice.toLowerCase():
                    case NotificationType.PurchaseTransactionRejectedNotice.toLowerCase():
                        // check if pathname is identical then use replace or push accordingly
                        if (checkIfDuplicatePathName(push.transactionId)) {
                            history.replace(`/purchaseTransaction/${push.transactionId}`, locationState);
                        } else {
                            history.push(`/purchaseTransaction/${push.transactionId}`, locationState);
                        }
                        break;

                    case NotificationType.SalesTransactionApprovalNotice.toLowerCase():
                    case NotificationType.SalesTransactionApprovedNotice.toLowerCase():
                    case NotificationType.SalesTransactionRejectedNotice.toLowerCase():
                    case NotificationType.UnawardNotice.toLowerCase():
                    case NotificationType.AwardedNotice.toLowerCase():
                        // check if pathname is identical then use replace or push accordingly
                        if (checkIfDuplicatePathName(push.transactionId)) {
                            history.replace(`/salesTransactions/${push.transactionId}`, locationState);
                        } else {
                            history.push(`/salesTransactions/${push.transactionId}`, locationState);
                        }
                        break;

                    case NotificationType.UpcomingBiddingNotice.toLowerCase():
                        history.replace('/allBiddingSessions', locationState);
                        break;

                    default:
                        break;
                }
            }
        });

        return () => {
            // unsubscribe
            PubSub.unsubscribe(token);
        };
    }, [flutterInterface, history, locationState, checkIfDuplicatePathName]);

    const context = useMemo(
        (): FlutterContext => ({
            ...meta,
            requestOneMonitoring: flutterInterface.getOneMonitoringData.bind(flutterInterface),
            initializePush: flutterInterface.initializePush.bind(flutterInterface),
            checkBiometric: flutterInterface.checkBiometric.bind(flutterInterface),
            sendDownloadFile: flutterInterface.sendDownloadFile.bind(flutterInterface),
            uploadFile: flutterInterface.uploadFile.bind(flutterInterface),
            uploadImage: flutterInterface.uploadImage.bind(flutterInterface),
        }),
        [meta, flutterInterface]
    );

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default FlutterProvider;
